import { MAP_BACKING_LAYERS, MAP_SETTINGS_OPTIONS } from 'constants/mapTypes';
import { LOGOUT_MODAL } from 'constants/modals';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ReactComponent as LogoutIcon } from 'images/newIcons/logout.svg';
import { MapType } from 'interfaces';
import { settingsSelector } from 'store/slices/mapV2/mapReducer/selectors';
import { settingsActions } from 'store/slices/mapV2/mapReducer/settingsSlice';
import { initialState as mapSettingsInitialState } from 'store/slices/mapV2/mapReducer/settingsSlice/constants';
import { typesSelector } from 'store/slices/mapV2/mapReducer/settingsSlice/selectors';
import { SettingObject } from 'store/slices/mapV2/mapReducer/settingsSlice/types';
import { modalsActions } from 'store/slices/service/modalsSlice';

import { DropdownSection } from 'components/DropdownSection';
import { IconButton } from 'components/ui/IconButton';
import { useMapSettingsLocalStorage } from 'utils/localStorage';

import { LineTextSwitch } from '../LineTextSwitch';

import { LayerTile } from './LayerTile';

export const MapSettings = () => {
  const mapTypes = useAppSelector(typesSelector);
  const mapSettings: Record<string, any> = useAppSelector(settingsSelector);

  const [, updateMapLocalSettings] = useMapSettingsLocalStorage(
    mapSettingsInitialState
  );

  const dispatch = useAppDispatch();

  const handleMapTypeActivate = (
    currentType: MapType,
    name: string,
    active: boolean,
    backing?: true
  ) => {
    const backingTypes = Object.fromEntries(
      Object.entries(mapTypes)
        .filter(([key, item]) => item.type === 'style')
        .map(([key, item]) => [key, { ...item, active: false }])
    );

    const updatedMapTypes = backing
      ? { ...mapTypes, ...backingTypes }
      : mapTypes;

    dispatch(
      settingsActions.setTypes({
        ...updatedMapTypes,
        [name]: {
          ...currentType,
          active,
        },
      })
    );
  };

  const handleMapLayerClick = (name: string) => {
    const currentType = mapTypes[name];

    if (currentType.type === 'style') {
      if (currentType.active) return;

      handleMapTypeActivate(currentType, name, true, true);

      return;
    }

    handleMapTypeActivate(currentType, name, !currentType.active);
  };

  const handleMapSettingToggle = (key: string, value: boolean) => {
    const settingObject = {
      key: key,
      value: !value,
    } as SettingObject;

    dispatch(settingsActions.setSetting(settingObject));
    updateMapLocalSettings(key, !value);
  };

  const handleExitModalOpen = () =>
    dispatch(modalsActions.addModal({ id: LOGOUT_MODAL, isOpen: true }));

  return (
    <div className="flex flex-col gap-4 h-full p-4 overflow-auto">
      <DropdownSection
        title="Подложка карты"
        classNames={{
          container: 'gap-4',
          content:
            'grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-x-2 gap-y-3',
        }}
      >
        {MAP_BACKING_LAYERS.map((item) => (
          <LayerTile
            key={item.name}
            id={item.name}
            src={item.preview}
            label={item.label}
            isActive={mapTypes[item.name].active}
            onClick={handleMapLayerClick}
          />
        ))}
      </DropdownSection>
      <DropdownSection
        title="Настройки карты"
        classNames={{
          container: 'gap-4',
          content: 'flex flex-col gap-3',
        }}
      >
        {MAP_SETTINGS_OPTIONS.map((option) => (
          <LineTextSwitch
            key={option.value}
            active={!!mapSettings[option.value]}
            text={option.label}
            icon={option.icon}
            classNames={{ text: 'tpg-b2', icon: 'w-5 h-5' }}
            onSwitch={() =>
              handleMapSettingToggle(option.value, !!mapSettings[option.value])
            }
          />
        ))}
      </DropdownSection>
      <IconButton
        title="Выход"
        icon={<LogoutIcon className="shrink-0 w-5 h-5" />}
        classNames={{
          container: 'sticky flex items-center gap-2 top-full',
          title: 'tpg-b2',
        }}
        onClick={handleExitModalOpen}
      />
    </div>
  );
};
